import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Check, CheckCheck, MoonStar } from 'lucide-react'
import { useCloseTopic, useMarkEngagedTopicDone, useSnoozeTopic, type EngagedTopic } from '../lib/api'
import SnoozePicker from './SnoozePicker'
import { isCodeChatId, TODO_CHAT_ID } from '../lib/chatNavigation'
import { confirmTodoistClose, isTodoistSourceKind } from '../lib/todoistCloseAlert'
import { useUi } from '../store/ui'

export default function EngagedTopicDoneButton({ topic }: { topic: EngagedTopic }) {
  const done = useMarkEngagedTopicDone()
  const close = useCloseTopic(topic.chat_id)
  const snooze = useSnoozeTopic(topic.chat_id)
  const [picking, setPicking] = useState(false)
  const qc = useQueryClient()
  const compact = useUi((s) => s.effectiveWidth < 560)
  const [closing, setClosing] = useState(false)
  const [closeError, setCloseError] = useState<string | null>(null)
  const busy = done.isPending || closing || close.isPending || snooze.isPending
  const snoozed = (topic.snoozed_until ?? 0) * 1000 > Date.now()
  const isCode = isCodeChatId(topic.chat_id)
  const isTodoist = !isCode && (topic.chat_id === TODO_CHAT_ID || isTodoistSourceKind(topic.source_kind))
  async function closeTopic() {
    if (busy) return
    setClosing(true)
    setCloseError(null)
    try {
      if (isTodoist && !await confirmTodoistClose({ topicIds: [topic.id] })) return
      await close.mutateAsync({ topic_id: topic.id, closeTodoist: isTodoist })
      qc.setQueryData<EngagedTopic[]>(['engaged-topics'], (current) => current?.filter((row) => (
        row.id !== topic.id || row.chat_id !== topic.chat_id
      )))
      void qc.invalidateQueries({ queryKey: ['engaged-topics'] })
      void qc.invalidateQueries({ queryKey: ['chats'] })
      void qc.invalidateQueries({ queryKey: ['work-monitor'] })
    } catch (error) {
      setCloseError(error instanceof Error ? error.message : 'Could not close. Tap the button to retry.')
    } finally {
      setClosing(false)
    }
  }
  async function snoozeTopic(wakeAt: number) {
    setPicking(false)
    try {
      await snooze.mutateAsync({ topic_id: topic.id, wake_at: wakeAt })
      setPicking(false)
      void qc.invalidateQueries({ queryKey: ['engaged-topics'] })
      void qc.invalidateQueries({ queryKey: ['work-monitor'] })
    } catch {
      setPicking(false)
    }
  }
  const buttonClass = 'inline-flex min-h-11 min-w-9 items-center justify-center gap-0.5 rounded-lg px-1.5 text-xs text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 dark:text-emerald-400 dark:hover:bg-emerald-950/40'
  return (
    <div className="mr-1 shrink-0">
      <div className="flex h-5 items-center">
        {/* Code conversations offer Archive and Snooze only (Mark, 1 November 2026). */}
        {!isCode && (
          <button
            type="button"
            aria-label={`Mark ${topic.title || 'Untitled topic'} done for now`}
            title="Hide from this list until you reply to this topic again. The topic stays open."
            disabled={busy || !topic.participation_message_id}
            onClick={() => done.mutate(topic)}
            className={buttonClass}
          >
            <Check className="h-3.5 w-3.5" aria-hidden />
            {!compact && (done.isPending ? 'Saving…' : 'for now')}
          </button>
        )}
        <button
          type="button"
          aria-label={isCode ? `Archive ${topic.title || 'Untitled topic'}` : isTodoist ? `Close ${topic.title || 'Untitled topic'} and complete its Todoist task` : `Mark ${topic.title || 'Untitled topic'} done`}
          title={isCode ? 'Archive this Code conversation and keep its history and saved session.' : isTodoist ? 'Close this topic and complete its Todoist task.' : 'Close this topic.'}
          disabled={busy}
          onClick={() => void closeTopic()}
          className={buttonClass}
        >
          <CheckCheck className="h-3.5 w-3.5" aria-hidden />
          {!compact && (closing ? 'Closing…' : isCode ? 'Archive' : isTodoist ? 'Close' : 'Done')}
        </button>
        <button
          type="button"
          aria-label={`${snoozed ? 'Reschedule snooze for' : 'Snooze'} ${topic.title || 'Untitled topic'}`}
          title={snoozed ? 'Reschedule this topic snooze.' : 'Snooze this topic.'}
          disabled={busy}
          onClick={() => setPicking(true)}
          className={snoozed
            ? 'inline-flex min-h-11 min-w-9 items-center justify-center rounded-lg px-1.5 text-amber-600 hover:bg-amber-50 disabled:opacity-50 dark:text-amber-400 dark:hover:bg-amber-950/40'
            : 'inline-flex min-h-11 min-w-9 items-center justify-center rounded-lg px-1.5 text-stone-500 hover:bg-stone-100 disabled:opacity-50 dark:text-stone-400 dark:hover:bg-stone-800'}
        >
          <MoonStar className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>
      {done.isError && <p role="alert" className="pb-2 text-xs text-red-600 dark:text-red-400">
        Could not save. Tap the for now button to retry.
      </p>}
      {snooze.isError && <p role="alert" className="pb-2 text-xs text-red-600 dark:text-red-400">
        Could not snooze. Tap the moon to retry.
      </p>}
      {picking && <SnoozePicker
        title={snoozed ? 'Reschedule topic' : 'Snooze topic'}
        onCancel={() => setPicking(false)}
        onPick={(wakeAt) => void snoozeTopic(wakeAt)}
      />}
      {closeError && <p role="alert" className="max-w-56 pb-2 text-xs text-red-600 dark:text-red-400">
        {closeError}
      </p>}
    </div>
  )
}
